import * as Actions from 'actionTypes';
import * as API from './client/api';
import { call, put, fork, takeLatest } from 'redux-saga/effects';
import { readCookie, setCookie, deleteCookie, options } from '../utils/cookies';
import { getPagePathname } from 'utils/getPagePathname';
import { initialize } from 'redux-form';

export function* watchResultsFetch(action) {
  try {
    const results = yield call(
      API.fetchResults,
      action.query,
      action.currentUrl
    );
    yield put({
      type: Actions.ADD_RESULTS,
      results
    });
  } catch (error) {
    console.error('[saga FETCH_RESULTS] failed:', error && error.message, {
      query: action.query,
      currentUrl: action.currentUrl,
      stack: error && error.stack
    });
    yield put({ type: Actions.ADD_RESULTS_FAILED, error });
  }
}

export function* watchPopularResultsFetch(action) {
  try {
    let results = [];
    let response;
    let page = 1;

    // Determine if the current page is a leasing page
    const isLeasingPage = getPagePathname() === 'car-leasing-deals';
    const updatedQuery = `${
      isLeasingPage
        ? '&advertisingLocation=autotraderLeasingCarsInStock&sort=leaseMonthlyPriceAsc'
        : ''
    }`;
    const minImagesRequired = isLeasingPage ? 1 : 4;

    while (results.length < 18) {
      response = yield call(
        API.fetchResults,
        action.query + `${updatedQuery}&page=${page}`,
        action.currentUrl
      );
      if (response) {
        results.push(
          ...response.results.filter(
            result => result.images && result.images.length > minImagesRequired
          )
        );
      }
      page++;
    }
    const uniqueCars = {};
    if (isLeasingPage) {
      results.forEach(car => {
        const key = car.derivative.make + ' ' + car.derivative.model;
        const price =
          car.leasingData &&
          car.leasingData.monthlyPayment &&
          car.leasingData.monthlyPayment.personal
            ? car.leasingData.monthlyPayment.personal
            : 0;

        if (
          !uniqueCars[key] ||
          price < uniqueCars[key].leasingData.monthlyPayment.personal
        ) {
          uniqueCars[key] = { ...car }; // Store the entire object
        }
      });
    }

    let carsunique = Object.values(uniqueCars);
    carsunique = carsunique.slice(0, 18);

    results = results.slice(0, 18);
    response.results = results;
    const randomlySortedResults =
      (results && results.length && results.sort(i => 0.5 - Math.random())) ||
      [];

    const sortByPriceResults =
      (isLeasingPage &&
        carsunique &&
        carsunique.length &&
        carsunique.sort(
          (a, b) =>
            a.leasingData.monthlyPayment.personal -
            b.leasingData.monthlyPayment.personal
        )) ||
      [];

    yield put({
      type: Actions.ADD_POPULAR_RESULTS,
      results: {
        ...response,
        results: isLeasingPage ? sortByPriceResults : randomlySortedResults
      }
    });
  } catch (error) {
    yield put({ type: Actions.ADD_POPULAR_RESULTS_FAILED, error });
  }
}

export function* watchInitialResultsFetch(action) {
  try {
    const results = yield call(
      API.fetchResults,
      action.query,
      action.currentUrl
    );
    yield put({
      type: Actions.ADD_INITIAL_RESULTS,
      results
    });
    if (results.initialForm) {
      yield put(initialize('search', results.initialForm));
    }
  } catch (error) {
    console.error(
      '[saga FETCH_INITIAL_RESULTS] failed:',
      error && error.message,
      {
        query: action.query,
        currentUrl: action.currentUrl,
        stack: error && error.stack
      }
    );
    yield put({ type: Actions.ADD_RESULTS_FAILED, error });
  }
}

export function* watchMakesFetch(action) {
  try {
    const results = yield call(API.fetchMakes, action.currentUrl);
    delete results.initialForm;
    yield put({
      type: Actions.ADD_RESULTS,
      results
    });
  } catch (error) {
    console.error('[saga FETCH_MAKES] failed:', error && error.message, {
      currentUrl: action.currentUrl,
      stack: error && error.stack
    });
    yield put({ type: Actions.ADD_RESULTS_FAILED, error });
  }
}

export function* checkPostcodeFetch(action) {
  try {
    const result = yield call(API.fetchPostcode, action.postcode);

    // Add valid postcode to cookie
    if (result && result.isValid) {
      yield call(setCookie, 'lastPostCode', action.postcode, options());
    }

    yield put({
      type: Actions.ADD_POSTCODE_RESULT,
      result
    });
  } catch (error) {
    yield put({ type: Actions.ADD_POSTCODE_RESULT_FAILED, error });
  }
}

export function* watchPostcodeFetch(action) {
  try {
    const lastPostCode = yield call(readCookie, 'lastPostCode');
    yield put({
      type: Actions.ADD_POSTCODE,
      lastPostCode
    });
  } catch (error) {
    yield put({ type: Actions.ADD_POSTCODE_FAILED, error });
  }
}

export function* watchPostcodeReset(action) {
  try {
    yield call(deleteCookie, 'lastPostCode', options());

    yield put({
      type: Actions.ADD_POSTCODE,
      lastPostCode: ''
    });
  } catch (error) {
    yield put({ type: Actions.ADD_POSTCODE_FAILED, error });
  }
}

export function* watchListingFetch(action) {
  try {
    const listing = yield call(API.fetchListing, action.id, action.postCode);
    yield put({
      type: Actions.ADD_LISTING,
      listing
    });
  } catch (error) {
    yield put({ type: Actions.ADD_LISTING_FAILED, error });
  }
}

export function* watchShortlistReviewsFetch() {
  try {
    const shortlistCookie = yield call(readCookie, 'shortlistCmsIds');
    const shortlistCmsIds = shortlistCookie || [];

    yield put({
      type: Actions.UPDATE_SHORTLIST,
      shortlistCmsIds
    });
  } catch (error) {
    yield put({ type: Actions.UPDATE_SHORTLIST_FAILED, error });
  }
}

export function* watchResultsRequests() {
  yield takeLatest(Actions.FETCH_RESULTS, watchResultsFetch);
}

export function* watchPopularResultsRequests() {
  yield takeLatest(Actions.FETCH_POPULAR_RESULTS, watchPopularResultsFetch);
}

export function* watchInitialResultsRequests() {
  yield takeLatest(Actions.FETCH_INITIAL_RESULTS, watchInitialResultsFetch);
}

export function* watchMakesRequests() {
  yield takeLatest(Actions.FETCH_MAKES, watchMakesFetch);
}

export function* watchCheckPostcodeRequests() {
  yield takeLatest(Actions.CHECK_POSTCODE, checkPostcodeFetch);
}

export function* watchFetchPostcodeRequests() {
  yield takeLatest(Actions.FETCH_POSTCODE, watchPostcodeFetch);
}

export function* watchResetPostcodeRequests() {
  yield takeLatest(Actions.RESET_POSTCODE, watchPostcodeReset);
}

export function* watchListingRequests() {
  yield takeLatest(Actions.FETCH_LISTING, watchListingFetch);
}

export function* watchShortlistReviews() {
  yield takeLatest(Actions.FETCH_SHORTLIST, watchShortlistReviewsFetch);
}

export default function* rootSaga(): Generator<*, *, *> {
  yield [
    fork(watchResultsRequests),
    fork(watchPopularResultsRequests),
    fork(watchInitialResultsRequests),
    fork(watchMakesRequests),
    fork(watchCheckPostcodeRequests),
    fork(watchListingRequests),
    fork(watchShortlistReviews),
    fork(watchFetchPostcodeRequests),
    fork(watchResetPostcodeRequests)
  ];
}



// WEBPACK FOOTER //
// ./src/App/sagas.js